import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { routes } from '@/lib/routes';
import type { ReportDetail as ReportDetailData } from '@/types/coach';
import { Button } from '@/components/ui/button';

const DATE_FMT = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const FIELDS: Array<{
  key: 'ausgangslage' | 'verlauf' | 'stand' | 'ausblick';
  label: string;
}> = [
  { key: 'ausgangslage', label: 'Ausgangslage' },
  { key: 'verlauf', label: 'Verlauf' },
  { key: 'stand', label: 'Aktueller Stand' },
  { key: 'ausblick', label: 'Ausblick' },
];

export function ReportDetail() {
  const { clientId, reportId } = useParams<{ clientId: string; reportId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<ReportDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{
    title: string;
    ausgangslage: string;
    verlauf: string;
    stand: string;
    ausblick: string;
  }>({ title: '', ausgangslage: '', verlauf: '', stand: '', ausblick: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.coach.reports
      .get(reportId)
      .then((res) => {
        if (!cancelled) setReport(res.report);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  function startEdit() {
    if (!report) return;
    setDraft({
      title: report.title ?? '',
      ausgangslage: report.ausgangslage,
      verlauf: report.verlauf,
      stand: report.stand,
      ausblick: report.ausblick,
    });
    setEditing(true);
  }

  async function saveEdit() {
    if (!reportId) return;
    setSaving(true);
    try {
      const res = await api.coach.reports.update(reportId, {
        title: draft.title.trim() || null,
        ausgangslage: draft.ausgangslage,
        verlauf: draft.verlauf,
        stand: draft.stand,
        ausblick: draft.ausblick,
      });
      setReport(res.report);
      setEditing(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!reportId || !clientId) return;
    if (!confirm('Bericht wirklich löschen?')) return;
    try {
      await api.coach.reports.remove(reportId);
      navigate(routes.coach.client(clientId!));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    }
  }

  if (loading) {
    return (
      <div className="space-y-3 p-6">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }
  if (!report) return null;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(routes.coach.client(clientId!))}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          ← Zurück zum Klienten
        </button>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">
            {report.title || `Coaching-Bericht für ${report.snapshot.client.name}`}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Berichtsdatum: {DATE_FMT.format(new Date(report.reportDate))}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <a
            href={api.coach.reports.pdfUrl(report.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button size="sm" variant="outline">
              PDF herunterladen
            </Button>
          </a>
          {!editing && (
            <Button size="sm" variant="outline" onClick={startEdit}>
              Bearbeiten
            </Button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-4 rounded-md border border-border bg-card p-4">
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">Titel</span>
            <input
              type="text"
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </label>
          {FIELDS.map((f) => (
            <label key={f.key} className="block text-sm">
              <span className="mb-1 block font-medium">{f.label}</span>
              <textarea
                rows={6}
                className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                value={draft[f.key]}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
              />
            </label>
          ))}
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Abbrechen
            </Button>
            <Button size="sm" onClick={saveEdit} disabled={saving}>
              {saving ? 'Speichern…' : 'Speichern'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4 rounded-md border border-border bg-card p-4">
          {FIELDS.map((f) => (
            <section key={f.key}>
              <h2 className="mb-1 text-sm font-semibold">{f.label}</h2>
              <p className="whitespace-pre-line text-sm text-foreground/90">
                {report[f.key] || (
                  <span className="italic text-muted-foreground">(keine Inhalte)</span>
                )}
              </p>
            </section>
          ))}
        </div>
      )}

      {report.snapshot.milestones.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold">
            Meilensteine zum Berichts-Zeitpunkt
          </h2>
          <ul className="space-y-2 text-sm">
            {report.snapshot.milestones.map((m) => (
              <li
                key={m.id}
                className="rounded-md border border-border bg-card px-3 py-2"
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {DATE_FMT.format(new Date(m.date))}
                </div>
                <div className="font-medium">{m.title}</div>
                {m.description && (
                  <p className="mt-0.5 text-xs text-foreground/80">{m.description}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {report.snapshot.sessions.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold">Sessions zum Berichts-Zeitpunkt</h2>
          <ul className="space-y-2 text-sm">
            {report.snapshot.sessions.map((s, idx) => (
              <li
                key={s.id}
                className="rounded-md border border-border bg-card px-3 py-2"
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {DATE_FMT.format(new Date(s.date))}
                  {s.durationMinutes != null && ` · ${s.durationMinutes} Min`}
                </div>
                <div className="font-medium">
                  {idx === 0 ? 'Erstgespräch' : `Session ${idx + 1}`}
                </div>
                {s.keyInsightsSnippet && (
                  <p className="mt-1 text-xs">
                    <span className="text-muted-foreground">Erkenntnisse:</span>{' '}
                    {s.keyInsightsSnippet}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="border-t border-border pt-4">
        <Button size="sm" variant="ghost" onClick={handleDelete}>
          Bericht löschen
        </Button>
      </div>
    </div>
  );
}
